import { LogOut, Shield, User } from 'lucide-react';
import { Button } from '../../../components/ui/button.js';
import { useAuth } from '../../../lib/auth-context.js';
import { isCustomerAdmin } from '../../../lib/roles.js';
import { DemoModeToggle } from '../../demo/index.js';
import { BrandingSettings } from '../components/BrandingSettings.js';

/**
 * Per-user settings. Today shows the JWT-derived identity + the
 * sign-out CTA. Stage 2 adds preferences (theme, default landing
 * page, notification channel) backed by a `/v1/user/settings` PUT.
 */
export function UserSettingsPage(): JSX.Element {
  const { session, signOut } = useAuth();
  if (!session) {
    return <div className="p-8 text-sm text-foreground/60">Not signed in.</div>;
  }

  const expiresAt = new Date(session.exp * 1000);
  const expiresInMs = session.exp * 1000 - Date.now();
  const expiresHumane =
    expiresInMs > 0 ? `in ${Math.round(expiresInMs / 1000 / 60)} min` : 'expired';

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">User Settings</h1>
        <p className="text-sm text-foreground/60">
          Per-user identity + session controls. Preferences arrive in Stage 2.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <User className="h-4 w-4" /> Identity
          </header>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
            <Row label="Email" value={session.email ?? '—'} />
            <Row label="Workfront ID" value={session.workfrontUserId} mono />
            <Row label="Client ID" value={session.clientId} mono />
            <Row label="License" value={session.licenseId} mono />
          </dl>
        </section>

        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <Shield className="h-4 w-4" /> Session
          </header>
          <dl className="grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
            <Row label="Roles" value={session.roles.length > 0 ? session.roles.join(', ') : '—'} />
            <Row label="Token expires" value={expiresAt.toLocaleString()} />
            <Row label="Time remaining" value={expiresHumane} />
          </dl>
        </section>
      </div>

      {isCustomerAdmin(session) ? (
        <div className="px-6 py-3">
          <BrandingSettings />
        </div>
      ) : null}

      <div className="px-6 py-3">
        <DemoModeToggle />
      </div>

      <div className="px-6 py-3">
        <Button
          type="button"
          onClick={() => void signOut()}
          variant="outline"
          className="border-foreground/15 bg-foreground/5 text-foreground/80 hover:border-rose-400/40 hover:text-rose-800 dark:hover:text-rose-200"
        >
          <LogOut className="mr-1.5 h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="text-xs text-foreground/50">{label}</dt>
      <dd className={mono ? 'font-mono text-xs text-foreground/85' : 'text-sm text-foreground/85'}>
        {value}
      </dd>
    </>
  );
}
